/**
 * Applies sound-based distortion to coordinates
 * @param {Object} normalizedPoint - Normalized {x, y} point (0-1)
 * @param {number} width - Canvas width
 * @param {number} height - Canvas height
 * @param {Array} soundSpectrum - Array of sound frequency data
 * @param {number} ogx - Original X coordinate
 * @param {number} ogy - Original Y coordinate
 * @returns {Object} Distorted point {x, y}
 */
export const soundDistortion = ({ x, y }, width, height, soundSpectrum, ogx, ogy) => {
  const spectrum = Array.isArray(soundSpectrum) ? soundSpectrum : [0, 0, 0, 0, 0, 0, 0, 0];
  const dx = (ogx / width - 0.5) * (width / height); // Normalize X relative to Y
  const dy = ogy / height - 0.5;
  const centerDistance = Math.sqrt(dx * dx + dy * dy);
  const reverseCenterDistance = 1 - centerDistance;
  const angle = Math.atan2(dy, dx);

  // Bunch together lower 30% of frequencies for center particles
  let loudness;
  const lowerFreqThreshold = 0.3; // Center 30% of particles

  if (centerDistance < lowerFreqThreshold) {
    // Center particles: average the lower 30% of frequency bands
    const numLowBands = Math.floor(spectrum.length * 0.3);
    let sum = 0;
    for (let i = 0; i < numLowBands; i++) {
      sum += spectrum[i] || 0;
    }
    loudness = sum / numLowBands;
  } else {
    // Outer particles: map to remaining frequencies
    const normalizedDistance = (centerDistance - lowerFreqThreshold) / (1 - lowerFreqThreshold);
    const startBand = Math.floor(spectrum.length * 0.3);
    const band = Math.min(startBand + Math.round(normalizedDistance * (spectrum.length - 1 - startBand)), spectrum.length - 1);
    loudness = spectrum[band] ?? 0;
  }

  const compresh = (100 - reverseCenterDistance * 100);
  loudness = loudness / compresh;

  // Distortion magnitude with a cubic falloff and noise
  const baseDistortion = loudness * Math.pow(centerDistance, 3);

  // Add subtle directional noise
  const noiseStrength = 0.00125;
  const noiseX = (Math.random() - 0.5) * noiseStrength;
  const noiseY = (Math.random() - 0.5) * noiseStrength;

  return {
    x: x + Math.cos(angle) * baseDistortion + noiseX * loudness,
    y: y + Math.sin(angle) * baseDistortion + noiseY * loudness
  };
};
